import { Temporal } from 'temporal-polyfill'
import { toPairs } from 'lodash'
import { pad } from './string.ts'
import { roundUp } from './math.ts'
import TimetoastTemporal from '@base/timetoast_temporal.js'
import { MAGNITUDES } from '@constants/date.js'

// Note: These utility functions should assume years are in the Proleptic Gregorian calendar. This means that year 0 is 1 BCE
// E.g. year 1 is 1 AD, 0 is 1 BCE, -9999 is 10,000 BCE
const ONE_BC = 0
const ONE_AD = 1

export function toUTCDateTimeString(temporal) {
  // note need to pad
  const dateArray = [pad(temporal.year), pad(temporal.month), pad(temporal.day)]
  const timeArray = [pad(temporal.hours), pad(temporal.minutes), pad(temporal.seconds)]
  return dateArray.join('-') + ' ' + timeArray.join(':')
}

export function getDaysBetween(startTT, endTT) {
  const { temporal: startTemporal } = startTT
  const { temporal: endTemporal } = endTT
  let result = []
  // get the elapsed days, but add an extra day as we may need to span slightly more than the
  // whole days elapsed to return correct date values from this function; we do an out-of-bounds
  // check before returning anything anyway
  const numDays = elapsedDays(startTT, endTT) + 1
  let temporal, betweenBounds
  for (let i = 1; i <= numDays; i++) {
    // add i days, and bring it back to start of the day
    temporal = startTemporal.add({ days: i }).startOfDay()

    // Note: Only add the date if it's actually between the start and end bound (so not including the start and end)
    betweenBounds =
      temporal.epochMilliseconds > startTemporal.epochMilliseconds &&
      temporal.epochMilliseconds < endTemporal.epochMilliseconds
    if (betweenBounds) {
      result.push(temporal)
    }
  }
  return result
}

// Note: This function could easily be refactored to create a separate function that does
// the day of the week calc so we could use it for sundays, wednesdays etc.
export function getStartsOfWeekBetween(startTT, endTT) {
  const { temporal: startTemporal } = startTT
  const { temporal: endTemporal } = endTT

  let result = []

  let weekDay = 1 // we want to calculate mondays
  const daysToAdd = (weekDay + 7 - startTemporal.dayOfWeek) % 7
  const first = startTemporal.add({ days: daysToAdd }).startOfDay()

  // start by adding our first one
  let outOfBounds = false
  let iteration = 0
  let current = first
  while (!outOfBounds) {
    if (iteration > 0) {
      current = first.add({ days: iteration * 7 })
    }
    // check if less than end temporal
    if (Temporal.PlainDate.compare(current, endTemporal) < 0) {
      result.push(current)
    } else {
      outOfBounds = true
      break
    }
    iteration++
  }
  return result
}

export function getMonthsBetween(startTT, endTT) {
  const { temporal: startTemporal } = startTT
  const { temporal: endTemporal } = endTT
  let result = []

  const startYear = startTemporal.year
  const endYear = endTemporal.year
  let year, startMonth, endMonth, numMonths, current

  // check if dates span multiple years
  if (startYear != endYear) {
    let years = getAllYears(startTT, endTT)
    const numYears = years.length

    for (let i = 0; i < numYears; i++) {
      year = years[i]
      // for each year
      if (year === startYear) {
        // add all months after the start month
        startMonth = startTemporal.month + 1
        for (let j = startMonth; j <= 12; j++) {
          current = TimetoastTemporal.fromUTC(year, j, 1, 0, 0, 0, 0).temporal
          result.push(current)
        }
      } else if (year === endYear) {
        // add all months up to, and including, the end month
        endMonth = endTemporal.month
        for (let k = 1; k <= endMonth; k++) {
          current = TimetoastTemporal.fromUTC(year, k, 1, 0, 0, 0, 0).temporal
          result.push(current)
        }
      } else {
        // must be a year between the first and last year, so just add all it's months
        for (let l = 1; l <= 12; l++) {
          current = TimetoastTemporal.fromUTC(year, l, 1, 0, 0, 0).temporal
          result.push(current)
        }
      }
    }
  } else {
    // years are the same, so push the first of each month between the two dates into the dates array
    year = startYear // or endYear, they're the same
    startMonth = startTemporal.month
    endMonth = endTemporal.month
    numMonths = endMonth - startMonth
    // Note: Start loop at 1 so we start one month later than the first date's month
    for (let i = 1; i < numMonths; i++) {
      current = TimetoastTemporal.fromUTC(year, startMonth + i, 1, 0, 0, 0, 0).temporal
      result.push(current)
    }
  }
  return result
}

export function getHalfYearsBetween(startTT, endTT) {
  const { temporal: startTemporal } = startTT
  const { temporal: endTemporal } = endTT
  let result = []
  let current = startTemporal

  if (startTemporal.month < 7) {
    // we want the middle of the same year ()
    current = TimetoastTemporal.fromUTC(startTemporal.year, 7, 1, 0, 0, 0, 0).temporal
  } else if (startTemporal.month >= 7) {
    // we want the start of next year
    current = TimetoastTemporal.fromUTC(startTemporal.year + 1, 1, 1, 0, 0, 0, 0).temporal
  }
  if (Temporal.PlainDate.compare(current, endTemporal) < 0) {
    result.push(current)
  }
  let outOfBounds = false
  while (!outOfBounds) {
    // while the date is less than
    current = current.add({ months: 6 })
    // only keep going if before end date
    if (Temporal.PlainDate.compare(current, endTemporal) < 0) {
      // while the temporal to add is before
      result.push(current)
    } else {
      outOfBounds = true
    }
  }
  return result
}

export function getYearsBetween(startTT, endTT) {
  const { temporal: startTemporal } = startTT
  const { temporal: endTemporal } = endTT
  let result = []
  let year = startTemporal.year + 1
  const endYear = endTemporal.year

  let temporal
  while (year <= endYear) {
    temporal = TimetoastTemporal.fromUTC(year, 1, 1, 0, 0, 0, 0).temporal
    result.push(temporal)
    year++
  }
  return result
}

export function getHalfDecadesBetween(startTT, endTT) {
  const { temporal: startTemporal } = startTT
  const { temporal: endTemporal } = endTT
  let result = []
  const lustrum = 5

  const startYear = startTemporal.year
  const endYear = endTemporal.year
  let year = getEndOfHalfDecade(startYear)
  let nextYear
  // then keep collecting lustrum years
  while (year <= endYear) {
    result.push(TimetoastTemporal.fromUTC(year, 1, 1, 0, 0, 0, 0).temporal)
    // now add a lustrum (whilst dealing with possibility we are crossing the BC/AD divide)
    nextYear = year + lustrum
    if (year < ONE_BC && nextYear >= ONE_AD) {
      nextYear = ONE_BC // just crossed the BC/AD point, adjust to restart at 0 (1 BCE)
    }
    year = nextYear
  }
  return result
}

export function getDecadesBetween(startTT, endTT) {
  const { temporal: startTemporal } = startTT
  const { temporal: endTemporal } = endTT
  let result = []
  const decade = 10

  const startYear = startTemporal.year
  const endYear = endTemporal.year
  let year = getEndOfDecade(startYear)
  let nextYear
  // then keep collecting decade years
  while (year <= endYear) {
    result.push(TimetoastTemporal.fromUTC(year, 1, 1, 0, 0, 0, 0).temporal)
    nextYear = year + decade
    if (year < ONE_BC && nextYear >= ONE_AD) {
      nextYear = ONE_BC // just crossed the BC/AD point, adjust to restart at 0 (1 BCE)
    }
    year = nextYear
  }
  return result
}

export function getHalfCenturiesBetween(startTT, endTT) {
  const { temporal: startTemporal } = startTT
  const { temporal: endTemporal } = endTT
  let result = []
  const halfCentury = 50

  const startYear = startTemporal.year
  const endYear = endTemporal.year
  let year = getEndOfHalfCentury(startYear)
  let nextYear
  while (year <= endYear) {
    result.push(TimetoastTemporal.fromUTC(year, 1, 1, 0, 0, 0, 0).temporal)
    nextYear = year + halfCentury
    if (year < ONE_BC && nextYear >= ONE_AD) {
      nextYear = ONE_BC // just crossed the BC/AD point, adjust to restart at 0 (1 BCE)
    }
    year = nextYear
  }
  return result
}

export function getCenturiesBetween(startTT, endTT) {
  const { temporal: startTemporal } = startTT
  const { temporal: endTemporal } = endTT
  let result = []
  const century = 100

  const startYear = startTemporal.year
  const endYear = endTemporal.year
  let year = getEndOfCentury(startYear)
  let nextYear
  while (year < endYear) {
    result.push(TimetoastTemporal.fromUTC(year, 1, 1, 0, 0, 0, 0).temporal)
    nextYear = year + century
    if (year < ONE_BC && nextYear >= ONE_AD) {
      nextYear = ONE_BC // just crossed the BC/AD point, adjust to restart at 0 (1 BCE)
    }
    year = nextYear
  }
  return result
}

export function getHalfMilleniaBetween(startTT, endTT) {
  const { temporal: startTemporal } = startTT
  const { temporal: endTemporal } = endTT
  let result = []
  const halfMillenium = 500

  const startYear = startTemporal.year
  const endYear = endTemporal.year
  let year = getEndOfHalfMillenium(startYear)
  let nextYear
  while (year <= endYear) {
    result.push(TimetoastTemporal.fromUTC(year, 1, 1, 0, 0, 0, 0).temporal)
    nextYear = year + halfMillenium
    if (year < ONE_BC && nextYear >= ONE_AD) {
      nextYear = ONE_BC // just crossed the BC/AD point, adjust to restart at 0 (1 BCE)
    }
    year = nextYear
  }
  return result
}

export function getMilleniaBetween(startTT, endTT) {
  const { temporal: startTemporal } = startTT
  const { temporal: endTemporal } = endTT
  let result = []
  const millenium = 1000

  const startYear = startTemporal.year
  const endYear = endTemporal.year
  let year = getEndOfMillenium(startYear)
  let nextYear
  while (year < endYear) {
    result.push(TimetoastTemporal.fromUTC(year, 1, 1, 0, 0, 0, 0).temporal)
    nextYear = year + millenium
    if (year < ONE_BC && nextYear >= ONE_AD) {
      nextYear = ONE_BC // just crossed the BC/AD point, adjust to restart at 0 (1 BCE)
    }
    year = nextYear
  }
  return result
}

export function getHalfDecamilleniaBetween(startTT, endTT) {
  const { temporal: startTemporal } = startTT
  const { temporal: endTemporal } = endTT
  let result = []
  const halfDeccamillenium = 5000

  const startYear = startTemporal.year
  const endYear = endTemporal.year
  let year = getEndOfHalfDecamillenium(startYear)
  let nextYear
  while (year < endYear) {
    result.push(TimetoastTemporal.fromUTC(year, 1, 1, 0, 0, 0, 0).temporal)
    nextYear = year + halfDeccamillenium
    if (year < ONE_BC && nextYear >= ONE_AD) {
      nextYear = ONE_BC // just crossed the BC/AD point, adjust to restart at 0 (1 BCE)
    }
    year = nextYear
  }
  return result
}

export function getDecamilleniaBetween(startTT, endTT) {
  const { temporal: startTemporal } = startTT
  const { temporal: endTemporal } = endTT
  let result = []
  const decaMillenium = 10000

  const startYear = startTemporal.year
  const endYear = endTemporal.year
  let year = getEndOfDecamillenium(startYear)
  let nextYear
  while (year < endYear) {
    result.push(TimetoastTemporal.fromUTC(year, 1, 1, 0, 0, 0, 0).temporal)
    nextYear = year + decaMillenium
    if (year < ONE_BC && nextYear >= ONE_AD) {
      nextYear = ONE_BC // just crossed the BC/AD point, adjust to restart at 0 (1 BCE)
    }
    year = nextYear
  }
  return result
}

// Helpers
// Note: These functions expect proleptic calendar years (i.e. 0 equal 1 BCE, -9 equal 10 BCE)

// Converts year on proleptic calendar into the era year (where year 0 equal 1 BCE, -1 equal 2 BCE)
// This is uesful to figure out if this is a 'major' year when dealing with BCE dates (i.e. a decade year)
function prolepticToEraYear(prolepticYear) {
  return prolepticYear <= 0 ? Math.abs(prolepticYear) + 1 : prolepticYear
}

export function isHalfDecadeYear(prolepticYear) {
  if (prolepticYear === 0) {
    return true
  }
  return prolepticToEraYear(prolepticYear) % 5 === 0
}

export function isDecadeYear(prolepticYear) {
  if (prolepticYear === 0) {
    return true
  }
  return prolepticToEraYear(prolepticYear) % 10 === 0
}

export function isHalfCenturyYear(prolepticYear) {
  if (prolepticYear === 0) {
    return true
  }
  return prolepticToEraYear(prolepticYear) % 50 === 0
}

export function isCenturyYear(prolepticYear) {
  if (prolepticYear === 0) {
    return true
  }
  return prolepticToEraYear(prolepticYear) % 100 === 0
}

export function isHalfMilleniumYear(prolepticYear) {
  if (prolepticYear === 0) {
    return true
  }
  return prolepticToEraYear(prolepticYear) % 500 === 0
}

export function isMilleniumYear(prolepticYear) {
  if (prolepticYear === 0) {
    return true
  }
  return prolepticToEraYear(prolepticYear) % 1000 === 0
}

export function isHalfDecamilleniumYear(prolepticYear) {
  if (prolepticYear === 0) {
    return true
  }
  return prolepticToEraYear(prolepticYear) % 5000 === 0
}

export function isDecamilleniumYear(prolepticYear) {
  if (prolepticYear === 0) {
    return true
  }
  return prolepticToEraYear(prolepticYear) % 10000 === 0
}

export function elapsedDays(startTT, endTT) {
  const { temporal: startTemporal } = startTT
  const { temporal: endTemporal } = endTT
  return Math.floor((endTemporal.epochMilliseconds - startTemporal.epochMilliseconds) / MAGNITUDES.day)
}

/*

  Get beginning and end years of particular eras
  If we request beginng of a magnitude, we return the year. For instance if we want beginning of decade for the year 100, we return 100 because the date's month/day could push the original date.
  If we want the end of a magnitude, we return the next one up.

*/

function getEndYear(prolepticYear, chkFn, yearsInMagnitude) {
  if (prolepticYear >= 0) {
    // 1 BCE, or AD year
    return chkFn(prolepticYear) ? prolepticYear + yearsInMagnitude : roundUp(prolepticYear, yearsInMagnitude)
  } else {
    // 2 BCE or further back
    if (chkFn(prolepticYear)) {
      return prolepticYear + yearsInMagnitude
    } else {
      // use roundUp, but then adjust it by 1 to ensure correct proleptic year, unless it would be in AD, then just go with 1 BCE
      const roundedUp = roundUp(prolepticYear, yearsInMagnitude) + 1
      return roundedUp > 0 ? 0 : roundedUp
    }
  }
}
// aka lustrum
export function getEndOfHalfDecade(prolepticYear) {
  return getEndYear(prolepticYear, isHalfDecadeYear, 5)
}

export function getEndOfDecade(prolepticYear) {
  return getEndYear(prolepticYear, isDecadeYear, 10)
}

export function getEndOfHalfCentury(prolepticYear) {
  return getEndYear(prolepticYear, isHalfCenturyYear, 50)
}

export function getEndOfCentury(prolepticYear) {
  return getEndYear(prolepticYear, isCenturyYear, 100)
}

export function getEndOfHalfMillenium(prolepticYear) {
  return getEndYear(prolepticYear, isHalfMilleniumYear, 500)
}

export function getEndOfMillenium(prolepticYear) {
  return getEndYear(prolepticYear, isMilleniumYear, 1000)
}

export function getEndOfHalfDecamillenium(prolepticYear) {
  return getEndYear(prolepticYear, isHalfDecamilleniumYear, 5000)
}

export function getEndOfDecamillenium(prolepticYear) {
  return getEndYear(prolepticYear, isDecamilleniumYear, 10000)
}

export function getAllYears(startTT, endTT) {
  const { temporal: startTemporal } = startTT
  const { temporal: endTemporal } = endTT
  const startYear = startTemporal.year
  const endYear = endTemporal.year

  const numYears = endYear - startYear + 1
  let years = []
  for (let i = 0; i < numYears; i++) {
    years.push(startYear + i)
  }
  return years
}

// TODO: Use a modified binary search to speed up this simple linear search
export function findClosestMagnitude(val) {
  let minDelta = null
  let minIndex = null
  let delta
  const magnitudes = toPairs(MAGNITUDES)
  for (let i = 0, len = magnitudes.length; i < len; i++) {
    delta = Math.abs(magnitudes[i][1] - Math.abs(val))
    if (minDelta === null || delta <= minDelta) {
      minDelta = delta
      minIndex = i
    }
  }
  return magnitudes[minIndex][0]
}

// Note: We can use duck-typing to determine type of object instead of using 'typeof obj' or 'obj.constructor.name'
// as using typeof on polyfilled Temporal objects is inconsistent across Temporal libraries and obj.constructor.name
// isn't useful post-minification (i.e. in production) as variables and class names get mangled
//
// Uses duck-typing to determine Temporal.PlainDate and Temporal.ZonedDateTime instances
export function isTemporalZonedDateTime(obj) {
  if (!obj) return false
  // @js-temporal/polyfill and temporal-polyfill are slightly different, but this should
  // work for both
  const hasSomethingTimezoney = typeof obj.timeZone === 'object' || typeof obj.timeZoneId === 'string'

  return hasSomethingTimezoney && typeof obj.toPlainDateTime === 'function'
}

export function isTemporalPlainDate(obj) {
  if (!obj) return false
  return (
    !isTemporalZonedDateTime(obj) &&
    typeof obj.year === 'number' &&
    typeof obj.month === 'number' &&
    typeof obj.day === 'number'
  )
}

export function isTemporalInstant(obj) {
  if (!obj) return false
  return typeof obj.toZonedDateTimeISO === 'function'
}

export function isTemporalPlainDateOrZonedDateTime(obj) {
  return isTemporalZonedDateTime(obj) || isTemporalPlainDate(obj)
}
